// Package imports:
import React from "react";
// Component imports:
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import ScrollSpy from "../../ui-elements/ScrollSpy/ScrollSpy";
import ScrollSpySection from "../../ui-elements/ScrollSpy/ScrollSpySection";
import BulletList from "../../ui-elements/Lists/BulletList";
import Link from "../../ui-elements/Link/Link";

const PepWatchDisclaimerPage: React.FC = () => {
  return (
    <div className="main KCL_disclaimer">
      <div className="mini_shell">
        {/* Header section with main title and introductory summary */}
        <HeadSection
          title="Skilmálar fyrir PEP vaktin"
          summary={
            <p>
              PEP vaktin er þjónusta sem gerir tilkynningarskyldum aðilum kleift að framkvæma reglubundna athugun á
              sínum viðskiptavinum með sjálfvirkum hætti. Vöktunin fer fram í umboði þjónustukaupa sem er ábyrgðaraðili
              í skilningi laga nr. 90/2018 um persónuvernd, og Keldan er vinnsluaðili við þá þjónustu. Ef þú hefur
              spurningar, vinsamlegast hafðu samband við okkur í gegnum{" "}
              <Link href="mailto:info@keldan.is" linkSize="20">
                info@keldan.is
              </Link>
              .
            </p>
          }
        />

        {/* Main content area containing the terms sections */}
        <div className="section__body">
          <div className="keldanTilkynningar">
            <article className="article-overview">
              <div className="article__body">
                {/* ScrollSpy wrapper provides navigation and smooth scrolling between sections */}
                <ScrollSpy
                  containerClass="article__content"
                  navClass="article__nav"
                  parentClass="keldanTilkynningar"
                  initialStep="thjonustulysing"
                >
                  {/* Section 1: Service Description */}
                  <ScrollSpySection className="article__section" id="thjonustulysing" label="Þjónustulýsing">
                    <h2>Þjónustulýsing</h2>
                    <p>
                      Sjálfvirk vöktun felur í sér reglulega skimun á móti PEP lista Keldunnar (stjórnmálaleg tengsl,
                      PEP) og/eða alþjóðlegum válistum. Þjónustan aðstoðar notendur við að uppfylla skyldur sínar
                      samkvæmt lögum nr. 140/2018 um aðgerðir gegn peningaþvætti og fjármögnun hryðjuverka.
                    </p>
                    <p>
                      Þjónustan framkvæmir sjálfvirka uppflettingu á skráðum viðskiptavinum í umhverfi notanda, með
                      eftirfarandi þáttum:
                    </p>
                    <BulletList
                      items={[
                        <p key="dagleg-athugun">
                          dagleg athugun á móti PEP lista Keldunnar um stjórnmálaleg tengsl eftir kennitölu skráðra
                          viðskiptavina,
                        </p>,
                        <p key="arsfjordungsleg-athugun">ársfjórðungsleg athugun á móti alþjóðlegum válistum.</p>,
                      ]}
                    />
                    <p>
                      Niðurstöðum er ýmist streymt með API vefþjónustum, birtar í vefviðmóti kerfisins á Keldunni og eru
                      aðgengilegar þar í skýrslu formi.
                    </p>
                  </ScrollSpySection>

                  {/* Section 2: Data Security and Privacy */}
                  <ScrollSpySection
                    className="article__section"
                    id="gagnaoryggi-og-personuvernd"
                    label="Gagnaöryggi og persónuvernd"
                  >
                    <h2>Gagnaöryggi og persónuvernd</h2>
                    <BulletList
                      items={[
                        <p key="personuvernd">
                          Öll meðhöndlun persónuupplýsinga fer fram í samræmi við íslensk lög um persónuvernd og vinnslu
                          persónuupplýsinga.
                        </p>,
                        <p key="dulkodun">
                          Gögn eru dulkóðuð í hvíld (rest) og í flutningi (transit) og vistuð í öruggum gagnageymslum
                          innan Evrópska efnahagssvæðisins (EES).
                        </p>,
                        <p key="thridju-adilar">
                          Keldan veitir þriðju aðilum ekki aðgang að gögnum nema lög kveði á um annað.
                        </p>,
                      ]}
                    />
                  </ScrollSpySection>

                  {/* Section 3: Pricing and Payment Terms */}
                  <ScrollSpySection
                    className="article__section"
                    id="gjaldskra-og-greidslusalmaðar"
                    label="Gjaldskrá og greiðsluskilmálar"
                  >
                    <h2>Gjaldskrá og greiðsluskilmálar</h2>
                    <BulletList
                      items={[
                        <p key="askrift">
                          Þjónustan er veitt gegn mánaðarlegri áskrift sem miðast við fjölda virkra viðskiptavina í
                          vöktun á hverjum tíma.
                        </p>,
                        <p key="verdlagning">
                          Viðskiptavinir geta valið um áskriftarleiðir sem fela í sér fasta eða breytilega verðlagningu,
                          eftir umfangi og þörf.
                        </p>,
                        <p key="greidslur">
                          Greiðslur eru innheimtar mánaðarlega, nema um annað sé sérstaklega samið.
                        </p>,
                        <p key="vidbot">
                          Ef viðskiptavinur fer yfir samningsbundinn fjölda í vöktun, er hann gjaldfærður fyrir viðbót
                          samkvæmt gildandi verðskrá.
                        </p>,
                      ]}
                    />
                  </ScrollSpySection>

                  {/* Section 4: Liability Limitation */}
                  <ScrollSpySection className="article__section" id="abyrgdartakmorkun" label="Ábyrgðartakmörkun">
                    <h2>Ábyrgðartakmörkun</h2>
                    <BulletList
                      items={[
                        <p key="arei<danleiki">
                          Keldan leggur sig fram um að tryggja réttmæti og áreiðanleika gagna, en ber ekki ábyrgð á
                          afleiðingum sem kunna að hljótast af röngum eða göllum í upplýsingum frá eigin eða
                          utanaðkomandi gagnaheimildum.
                        </p>,
                        <p key="as-is">
                          Þjónustan er veitt „eins og hún er" (e. as is) án beinna eða óbeinna ábyrgðaryfirlýsinga.
                        </p>,
                      ]}
                    />
                  </ScrollSpySection>

                  {/* Section 5: Usage Terms */}
                  <ScrollSpySection className="article__section" id="notkunarskilmalar" label="Notkunarskilmálar">
                    <h2>Notkunarskilmálar</h2>
                    <p>
                      Notendur skulu einungis nýta kerfið í samræmi við gildandi lög og reglur, og bera sjálfir ábyrgð á
                      túlkun og framkvæmd vöktunar.
                    </p>
                    <p>Með notkun á kerfinu lýsir notandi því yfir og ábyrgist:</p>
                    <BulletList
                      items={[
                        <p key="tilkynningarskyldur">
                          að vera tilkynningarskyldur aðili í skilningi 17. tl. 3. gr. laga nr. 140/2018 um aðgerðir
                          gegn peningaþvætti og fjármögnun hryðjuverka;
                        </p>,
                        <p key="naudsyn">
                          að notkun á upplýsingum úr þjónustunni sé nauðsynleg við framkvæmd áreiðanleikakönnunar,
                          viðvarandi eftirlits eða vegna annarra skyldna tilkynningarskylds aðila samkvæmt
                          peningaþvættislögum; og
                        </p>,
                        <p key="eigin-thagu">
                          að upplýsingar úr þjónustunni verði aðeins notaðar í eigin þágu til að uppfylla skyldur
                          samkvæmt peningaþvættislögum og séu ekki seldar, afhentar, eða dreift með öðrum hætti.
                        </p>,
                      ]}
                    />
                  </ScrollSpySection>

                  {/* Section 6: Termination and End of Service */}
                  <ScrollSpySection
                    className="article__section"
                    id="uppsogn-og-lok-thjonustu"
                    label="Uppsögn og lok þjónustu"
                  >
                    <h2>Uppsögn og lok þjónustu</h2>
                    <BulletList
                      items={[
                        <p key="uppsogn-vidskiptavinar">
                          Viðskiptavinur getur sagt upp áskrift með 30 daga fyrirvara, nema um annað sé sérstaklega
                          samið.
                        </p>,
                        <p key="adgangur-fellur-nidur">
                          Við uppsögn fellur niður allur aðgangur að PEP vöktuninni að liðnum uppsagnarfresti.
                        </p>,
                        <p key="gogn-hlada-nidur">
                          Viðskiptavini verður boðið að hlaða niður eigin gögnum úr þjónustunni innan 14 daga frá
                          lokadegi áskriftar. Að þeim tíma liðnum verða gögn varanlega eytt úr kerfum Keldunnar, nema
                          lög krefjist lengri geymslu.
                        </p>,
                        <p key="greidsluskylda">
                          Ef greiðsluskylda samkvæmt samningi er ekki uppfyllt, áskilur Keldan sér rétt til að loka
                          fyrir þjónustuna án frekari fyrirvara.
                        </p>,
                        <p key="uppsogn-keldunnar">
                          Keldan áskilur sér rétt til að segja þjónustu upp með 30 daga fyrirvara, t.d. vegna
                          tæknilegra, lagalegra eða rekstrarlegra ástæðna.
                        </p>,
                      ]}
                    />
                  </ScrollSpySection>

                  {/* Section 7: Changes to Terms */}
                  <ScrollSpySection
                    className="article__section"
                    id="breytingar-a-skilmalum"
                    label="Breytingar á skilmálum"
                  >
                    <h2>Breytingar á skilmálum</h2>
                    <BulletList
                      items={[
                        <p key="rett-til-breytinga">
                          Keldan áskilur sér rétt til að breyta þessum skilmálum hvenær sem er. Breytingar taka gildi
                          við birtingu á vef Keldunnar eða innan PEP vöktunarviðmótsins, nema annað sé sérstaklega tekið
                          fram.
                        </p>,
                        <p key="tilkynning-um-breytingar">
                          Ef breytingar eru verulegar, verður viðskiptavini tilkynnt með tölvupósti eða með tilkynningu
                          í þjónustunni a.m.k. 30 dögum áður en þær taka gildi.
                        </p>,
                        <p key="samthykki-breytinga">
                          Með áframhaldandi notkun þjónustunnar eftir gildistöku breytinga samþykkir viðskiptavinur nýju
                          skilmálana.
                        </p>,
                      ]}
                    />
                  </ScrollSpySection>

                  {/* Section 8: Laws and Jurisdiction */}
                  <ScrollSpySection className="article__section" id="log-og-varnarthing" label="Lög og varnarþing">
                    <h2>Lög og varnarþing</h2>
                    <p>
                      Þessir skilmálar lúta íslenskum lögum. Rísi ágreiningur vegna þjónustunnar eða skilmálanna, skal
                      honum vísað til úrlausnar Héraðsdóms Reykjavíkur, nema lög mæli fyrir um annað.
                    </p>
                  </ScrollSpySection>
                </ScrollSpy>
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the component as default to maintain consistency with the existing codebase
export default PepWatchDisclaimerPage;
